/**
 * Money is integer cents everywhere it crosses the API boundary. These helpers are
 * the only place a currency value is allowed to become a float, which keeps
 * rounding decisions in one auditable spot.
 */

export function centsToDecimal(cents: number): number {
    return cents / 100
}

/** Bare numeric string, no symbol. Use for CSV/XLSX cells and form inputs. */
export function formatMoney(cents: number, decimals = 2): string {
    return (cents / 100).toFixed(decimals)
}

export function decimalToCents(dollars: number): number {
    return Math.round(dollars * 100)
}

/**
 * Localized display with a currency symbol. Grouped thousands matter at a glance
 * in a financial console -- `$1,204.00` and `$120,400` are one keystroke apart
 * in the raw value but very different decisions.
 */
export function formatCurrency(cents: number, currency = 'usd'): string {
    return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency.toUpperCase(),
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(cents / 100)
}

/** Explicit sign, for ledger rows where direction is the point. */
export function formatSignedCurrency(cents: number, currency = 'usd'): string {
    const formatted = formatCurrency(Math.abs(cents), currency)
    if (cents > 0) return `+${formatted}`
    if (cents < 0) return `-${formatted}`
    return formatted
}

export function formatCompactCurrency(cents: number, currency = 'usd'): string {
    return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency.toUpperCase(),
        notation: 'compact',
        maximumFractionDigits: 1
    }).format(cents / 100)
}

/**
 * Parses operator-typed dollar input into cents. Tolerates currency symbols,
 * thousands separators, comma or period decimals (`10.65` / `10,65`), and
 * surrounding whitespace; returns null when the input is not a usable number
 * so callers can surface a validation message rather than silently writing NaN.
 */
export function parseDollarsToCents(input: string): number | null {
    const cleaned = input.replace(/[^\d.,-]/g, '')
    if (!cleaned || cleaned === '-' || cleaned === '.' || cleaned === ',') return null

    const lastComma = cleaned.lastIndexOf(',')
    const lastPeriod = cleaned.lastIndexOf('.')
    let normalized: string

    if (lastComma !== -1 && lastPeriod !== -1) {
        // Both separators: the last one is the decimal (`1,204.65` / `1.204,65`).
        normalized = lastComma > lastPeriod ? cleaned.replace(/\./g, '').replace(',', '.') : cleaned.replace(/,/g, '')
    } else if (lastComma !== -1) {
        // Only commas. A 1–2 digit tail is a decimal (`10,65`); three or more is
        // a thousands group (`1,204`).
        const fraction = cleaned.slice(lastComma + 1)
        normalized = /^\d{1,2}$/.test(fraction)
            ? `${cleaned.slice(0, lastComma).replace(/,/g, '')}.${fraction}`
            : cleaned.replace(/,/g, '')
    } else {
        normalized = cleaned
    }

    if (!normalized || normalized === '-' || normalized === '.') return null
    const value = Number(normalized)
    if (!Number.isFinite(value)) return null
    return decimalToCents(value)
}

export function formatNumber(value: number): string {
    return new Intl.NumberFormat().format(value)
}

/**
 * A 0–1 score as a percentage. The API expresses match scores, completeness, and
 * source health this way, and nobody reads `0.8734` as "87%".
 */
export function formatRatio(value: number, decimals = 0): string {
    return `${((value ?? 0) * 100).toFixed(decimals)}%`
}

/** The same score as a whole-number percentage, for progress bars and aria values. */
export function ratioToPercent(value: number): number {
    return Number(((value ?? 0) * 100).toFixed(0))
}
